import { EntityInstanceModel } from '@models/module/entityInstance';

export type KeyValueType<K = string, V = string> = {
  key: K;
  value: V;
};

export type IdValueType<V = string, K = number> = {
  id: K;
  value: V;
};

export type KeyValueListType<TKey = string, TValue = string> = KeyValueType<TKey, TValue>;

export type KeyValueDescriptionType<TKey = string, TValue = string> = KeyValueType<TKey, TValue> &
  Partial<KeyValueIsUsedModel<TKey, TValue>> & {
    description?: string;
    /** When true, the option is excluded from select-all / deselect-all toggles. */
    excludeFromSelectAll?: boolean;
  };

export interface KeyValueModel<TKey, TValue> {
  key: TKey;
  value?: TValue;
  order?: number;
  description?: string;
  name?: string;
}

export interface KeyValueUserModel<TKey> extends KeyValueType<TKey, string> {
  userId: number;
  isUsed: boolean;
  isGroup: boolean;
  roleIds: number[];
}

export interface KeyValueIsUsedModel<TKey, TValue> extends KeyValueIsDisabledModel<TKey, TValue> {
  isUsed: boolean;
  isPending: boolean;
}

export interface KeyValueElevatedRoleModel<TKey, TValue> extends KeyValueIsUsedModel<TKey, TValue> {
  isElevated: boolean;
  invalid: boolean;
}

export interface KeyValueCategoryModel<TKey, TValue> extends KeyValueType<TKey, TValue> {
  number: string;
  subTotal: number;
  itemsCount?: number;
  expanded?: boolean;
  itemsLoaded?: boolean;
}

export interface KeyValueFormulaLevelFieldModel {
  relationId: number;
  level: number;
}

export interface KeyValueFormulaFieldModel<TKey> extends KeyValueModel<TKey, string> {
  levels: KeyValueFormulaLevelFieldModel[];
  relations: string;
  level: number;
  moduleId: number;
  parentModuleTypeIds: number[];
  relationNames: string[];
  isDate?: boolean;
}

export interface KeyValueUserRolesType<TKey, TValue> extends KeyValueIsUsedModel<TKey, TValue> {
  email: string;
  orgName: string;
  roles: KeyValueType<number, string>[];
}

export interface KeyValueUserExtendedType<TKey, TValue> extends KeyValueIsUsedModel<TKey, TValue> {
  email: string;
  orgId: number;
  orgName: string;
}

export interface KeyValueTypeModel<TKey> extends KeyValueModel<TKey, string> {
  typeId: number;
  type?: string;
  displayTypeId: number;
  dataSourceId?: number;
}

export interface KeyValueGroupTransitions<TKey> extends KeyValueModel<TKey, string> {
  isGroup: boolean;
  isConditional: boolean;
}

export interface KeyValueIsPrimary<TKey> extends KeyValueModel<TKey, string> {
  isPrimary: boolean;
}

export interface KeyValueRoleModel<TKey, TValue> extends KeyValueType<TKey, TValue> {
  roleId: number;
  userId: number;
}

export interface KeyValueIsDisabledModel<K = string, V = string> extends KeyValueType<K, V> {
  isDisabled: boolean;
}

export interface KeyValueIsDefaultModel<TKey, TValue> extends KeyValueType<TKey, TValue> {
  isDefault: boolean;
}

export interface KeyValueDefaultModel<TKey, TValue> extends KeyValueModel<TKey, TValue> {
  default: boolean;
  type: number;
}

export interface KeyValueRoleElevatedModel<TKey, TValue> extends KeyValueType<TKey, TValue> {
  elevated: boolean;
}

export interface KeyValueRelationModel<TKey, TValue> extends KeyValueModel<TKey, TValue> {
  otherModuleId: number;
  /** True for a self-relation. */
  isNested: boolean;
}

export interface KeyValueChildrenModel<TKey, TValue> extends KeyValueModel<TKey, TValue> {
  children: KeyValueChildrenModel<TKey, TValue>[];
}

export interface KeyValuePlaceholdersModel<TKey, TValue> extends KeyValueModel<TKey, TValue> {
  dataType: string;
  hasParameters: boolean;
  hasParametersDefined: boolean;
  parameters: string[];
}

export interface KeyValuePeriodModel<TKey, TValue> extends KeyValueType<TKey, TValue> {
  startDate: Date;
  endDate: Date;
  hasMultiplePayments: boolean;
  isMultiplePeriod: boolean;
  status: string;
  color: string;
  textColor: string;
  allowsPaymentRequest: boolean;
  completedPaymentsExistAfter: boolean;
  pendingPaymentsExistBefore: boolean;
  entityInstances: EntityInstanceModel[];
  isDraft: boolean;
  sovEntityInstanceId?: number;
  sovIdentifier?: string;
  entityInstanceId?: number;
}

export interface KeyValuesCountryCodeModel<TKey> extends KeyValueModel<TKey, string> {
  phonePrefix: string;
}
